<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import { devices } from 'src/stores/lists/devices';
	import { Loader, PriorityGrid, PropertyCard, Text } from '@Shelly/shelly-design-system';
	import { isThreeEM, real_id } from 'src/helpers/interface_helpers';
	import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';
	import { dateFormat } from 'src/helpers/date_helper';
	import { convertValueAndUnit } from 'src/helpers/utilities';

	export let device: Device;

	const id = real_id(device.id);
	let peakPowerData: {
		live: { value: string; unit: string; time: string }[];
		avg5: { value: string; unit: string; time: string }[];
	} | null = null;

	const mapPowerPickData = (item: { c: number; r: number; t: number }) => {
		const raw = item.c >= Math.abs(item.r) ? item.c : item.r;
		const formatted = convertValueAndUnit(raw, 'W');
		return {
			value: String(formatted.value),
			unit: formatted.unit,
			time: item.t > 0 ? dateFormat(item.t * 1000, 'H:i:s d/m') : '',
		};
	};

	const getPeakStatistics = async () => {
		const res = await CacheStatisticRequest.getData(
			'/v2/devices/get/',
			{
				//@ts-ignore
				ids: [id],
			},
			undefined,
			false,
			true,
		);
		if (res?.[0]?.peakPower) {
			const peakPower = res[0].peakPower;
			peakPowerData = {
				live: peakPower.live.map((item) => mapPowerPickData(item)),
				avg5: peakPower.avg5.map((item) => mapPowerPickData(item)),
			};
		}
		return peakPowerData;
	};
</script>

{#if $devices[id]?.cloud_options?.measure_peak_power}
	<div class="mb-s">
		{#await getPeakStatistics()}
			<div class="d-flex justify-content-center">
				<Loader />
			</div>
		{:then data}
			{#if data}
				<Text styleType="tiny" class="mb-s text-capitalize">
					{$format('title_pick_power', { default: 'Highest peak energy' })}
				</Text>
				<PriorityGrid class="mb-s" animate={false}>
					{#if isThreeEM(device?.type) && device?.mode != 'monophase'}
						{#each data.live as channel, index (index)}
							<PropertyCard
								priority="compact"
								value={channel.value ?? ''}
								unit={channel.unit}
								label={channel.time}
								icon="fa-solid fa-bolt"
							/>
						{/each}
					{:else}
						<PropertyCard
							priority="compact"
							value={data.live[device.channel]?.value ?? ''}
							unit={data.live[device.channel]?.unit}
							label={data.live[device.channel]?.time || ''}
							icon="fa-solid fa-bolt"
						/>
					{/if}
				</PriorityGrid>
				<Text styleType="tiny" class="mb-s text-capitalize">
					{$format('title_pick_power_avg', { default: 'Peak energy average' })}
				</Text>
				<PriorityGrid animate={false}>
					{#if isThreeEM(device?.type) && device?.mode != 'monophase'}
						{#each data.avg5 as channel, index (index)}
							<PropertyCard
								priority="compact"
								value={channel.value ?? ''}
								unit={channel.unit}
								label={channel.time}
								icon="fa-solid fa-bolt"
							/>
						{/each}
					{:else}
						<PropertyCard
							priority="compact"
							value={data.avg5[device.channel]?.value ?? ''}
							unit={data.avg5[device.channel]?.unit}
							label={data.avg5[device.channel]?.time || ''}
							icon="fa-solid fa-bolt"
						/>
					{/if}
				</PriorityGrid>
			{/if}
		{/await}
	</div>
{/if}
